<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-hidden">
        <loading :status="loading.form"/>
        <form class="flex flex-col flex-1 min-h-0" @submit.prevent="saveDocumentation">
            <div class="stage-page-header">
                <div class="stage-page-header-row">
                    <div class="stage-page-header-main">
                        <h1 class="stage-page-title">{{ $t('Documentation') }}</h1>
                    </div>
                    <div class="stage-page-header-actions" v-on-clickaway="closeActionDropdowns">
                        <button class="btn-stage-icon-plain" type="button" :title="$t('Back')" :aria-label="$t('Back')" @click="$router.push(documentationListPath)">
                            <svg-vue class="h-4 w-4" icon="font-awesome.regular.chevron-left"></svg-vue>
                        </button>
                        <div class="relative inline-flex items-center">
                            <button
                                class="btn-stage-icon-plain"
                                type="button"
                                title="Aktivitätshistorie"
                                aria-label="Aktivitätshistorie"
                                @click="toggleHistoryPopover"
                            >
                                <svg-vue class="h-4 w-4" icon="font-awesome.regular.rectangle-history"></svg-vue>
                            </button>
                            <div
                                v-show="historyPopoverOpen"
                                class="documentation-history-popover origin-top-right absolute top-full right-0 mt-2 w-72 rounded shadow-lg z-50 bg-white border border-ultralight overflow-hidden"
                                style="z-index: 60;"
                            >
                                <div class="stage-icon-menu-header">
                                    Änderungshistorie
                                </div>
                                <div class="max-h-80 overflow-y-auto">
                                    <template v-if="documentation.change_history && documentation.change_history.length > 0">
                                        <div
                                            v-for="entry in documentation.change_history"
                                            :key="'dashboard-history-popover-' + entry.id"
                                            class="documentation-history-item px-4 py-2 border-b border-ultralight last:border-b-0"
                                        >
                                            <div class="flex items-center">
                                                <img
                                                    :src="entry.user && entry.user.avatar !== 'gravatar' ? entry.user.avatar : (entry.user ? entry.user.gravatar : '')"
                                                    alt="Profilbild"
                                                    class="h-6 w-6 rounded-full object-cover mr-2"
                                                />
                                                <div class="min-w-0 flex items-center justify-between w-full gap-2">
                                                    <div class="text-xs font-medium text-gray-900 truncate">
                                                        {{ entry.user ? entry.user.name : '-' }}
                                                    </div>
                                                    <div class="text-xs text-gray-400 whitespace-nowrap">
                                                        {{ formatDateTime(entry.created_at) }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </template>
                                    <div v-else class="px-4 py-3 text-sm text-gray-500">
                                        Noch keine Änderungen vorhanden
                                    </div>
                                </div>
                            </div>
                        </div>
                        <button
                            v-if="canShowApprovalManagement"
                            class="btn-stage-icon-plain"
                            type="button"
                            title="Freigabeverwaltung"
                            aria-label="Freigabeverwaltung"
                            @click="openApprovalModal"
                        >
                            <svg-vue class="h-4 w-4" icon="font-awesome.regular.user-lock"></svg-vue>
                        </button>
                        <button
                            v-if="isAdmin"
                            class="btn-stage-icon-plain"
                            type="button"
                            title="Dokumentation umbenennen"
                            aria-label="Dokumentation umbenennen"
                            @click="openRenamePage"
                        >
                            <svg-vue class="h-4 w-4" icon="font-awesome.regular.pen-to-square"></svg-vue>
                        </button>
                        <button
                            v-if="documentation.can_delete"
                            class="btn-stage-icon-plain"
                            type="button"
                            title="Dokumentation löschen"
                            aria-label="Dokumentation löschen"
                            @click="openDeleteDocumentationModal"
                        >
                            <svg-vue class="h-4 w-4" icon="font-awesome.trash-alt-regular"></svg-vue>
                        </button>
                    </div>
                </div>
            </div>

            <div class="stage-page-gutters stage-page-fill pt-3 h-0">
                <div class="w-full h-full bg-white shadow sm:rounded border border-ultralight overflow-hidden">
                    <div class="flex h-full min-h-0 items-stretch">
                        <div ref="contentScrollContainer" class="flex-auto min-w-0 overflow-y-auto">
                            <div class="sticky top-0 z-10 stage-header-surface border-b border-ultralight">
                                <div class="flex items-stretch min-h-12">
                                    <div class="w-13 flex-shrink-0 border-r border-ultralight flex items-center justify-center text-gray-400">
                                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.book-open-cover"></svg-vue>
                                    </div>
                                    <div class="flex-1 min-w-0 px-4" :class="!isEditMode ? 'border-r border-ultralight' : ''">
                                        <div class="flex h-full items-center">
                                            <span class="block truncate text-sm font-medium text-gray-700">
                                                {{ documentation.title || $t('Documentation') }}
                                            </span>
                                        </div>
                                    </div>
                                    <div v-if="!isEditMode" class="w-14 flex-shrink-0 border-r border-ultralight lg:flex-1 lg:min-w-0 lg:min-w-[250px]">
                                        <div class="relative h-full">
                                            <div class="hidden h-full items-center pl-4 pr-9 lg:flex">
                                                <span class="block truncate text-sm leading-5 font-medium text-gray-700">
                                                    {{ $t('Jump to section') }}
                                                </span>
                                            </div>
                                            <div class="pointer-events-none absolute inset-y-0 right-0 z-10 flex items-center pr-4 text-gray-400">
                                                <svg-vue class="h-[0.875rem] w-[0.875rem]" icon="font-awesome.regular.angle-down"></svg-vue>
                                            </div>
                                            <select
                                                v-model="selectedMenuAnchorId"
                                                class="block form-select absolute inset-0 z-20 w-full h-full border-0 rounded-none bg-transparent pl-4 pr-9 py-0 text-sm leading-5 font-medium text-gray-700 cursor-pointer opacity-0"
                                                @change="onMenuAnchorChange"
                                            >
                                                <option disabled value="">{{ $t('Jump to section') }}</option>
                                                <option
                                                    v-for="item in menuAnchors"
                                                    :key="'doc-menu-anchor-' + item.id"
                                                    :value="item.id"
                                                >
                                                    {{ item.label }}
                                                </option>
                                            </select>
                                        </div>
                                    </div>
                                    <div v-if="documentation.can_edit" class="w-36 flex-shrink-0" :class="isEditMode ? 'border-l border-ultralight' : ''">
                                        <button
                                            v-if="!isEditMode"
                                            class="btn-stage-primary w-full h-full rounded-none"
                                            style="border-radius: 0;"
                                            type="button"
                                            @click.prevent.stop="startEditMode"
                                        >
                                            {{ $t('Edit') }}
                                        </button>
                                        <button
                                            v-else
                                            class="btn-stage-primary w-full h-full rounded-none"
                                            style="border-radius: 0;"
                                            type="submit"
                                        >
                                            {{ $t('Save') }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div class="px-8 py-8">
                                <div v-if="isEditMode" class="documentation-editor">
                                    <input-wysiwyg
                                        id="documentation_body_manage"
                                        v-model="documentation.body"
                                        :readonly="!documentation.can_edit"
                                        :plugins="{images: true, cannedReply: false, attachment: true, shortCode: false, menuPoint: true, headings: true}"
                                        @selectUploadFile="selectUploadFile"
                                    >
                                        <template v-slot:top>
                                            <div :class="{'bg-gray-200': uploadingFileProgress > 0}" class="h-1 w-full">
                                                <div :style="{width: uploadingFileProgress + '%'}" class="bg-blue-500 py-0.5"></div>
                                            </div>
                                        </template>
                                    </input-wysiwyg>
                                </div>
                                <div v-else class="documentation-view-body">
                                    <div class="flex-1 min-w-0">
                                        <div
                                            class="ticket-reply-body text-gray-700"
                                            v-html="documentation.body && documentation.body.length > 0 ? documentation.body : '<p class=&quot;text-gray-400&quot;>Keine Inhalte vorhanden</p>'"
                                        ></div>
                                    </div>
                                </div>
                                <div v-if="documentation.attachments.length > 0" class="mt-4 pb-4">
                                    <div class="flex flex-wrap items-start gap-2">
                                        <template v-for="(attachment, index) in documentation.attachments">
                                            <attachment
                                                :details="attachment"
                                                :remove-button="documentation.can_edit && isEditMode"
                                                v-on:remove="removeAttachment(index)"
                                            />
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <aside :style="{flex: '0 0 300px', width: '300px'}" class="documentation-sidebar hidden xl:block self-stretch bg-white border-l border-ultralight overflow-y-auto">
                            <div class="p-6">
                                <div v-if="documentation.user">
                                    <div class="flex items-center">
                                        <img
                                            :src="documentation.user.avatar !== 'gravatar' ? documentation.user.avatar : documentation.user.gravatar"
                                            alt="Avatar"
                                            class="h-14 w-14 rounded-full object-cover flex-shrink-0"
                                        />
                                        <div class="ml-4 min-w-0 flex-1">
                                            <div class="mb-2 font-semibold text-base text-gray-800 truncate">{{ documentation.user.name }}</div>
                                            <div class="mb-1 text-sm leading-4 text-gray-500 truncate">{{ documentation.user.email || '-' }}</div>
                                            <div v-if="documentationOwnerPhone" class="text-sm leading-4 text-gray-500 truncate">{{ documentationOwnerPhone }}</div>
                                        </div>
                                    </div>
                                    <div class="my-6 border-t border-ultralight"></div>
                                </div>

                                <div v-if="canSeeReadonlyControl" class="mb-6">
                                    <div class="text-xs tracking-wide text-gray-500 mb-2">{{ $t('Schreibschutz intern') }}</div>
                                    <label class="flex items-center cursor-pointer">
                                        <input
                                            v-model="documentation.is_readonly"
                                            class="form-checkbox h-4 w-4 text-gray-600"
                                            type="checkbox"
                                            @change="savePermissionChanges()"
                                        >
                                        <span class="ml-2 text-sm text-gray-900">{{ $t('Schreibgeschützt') }}</span>
                                    </label>
                                </div>

                                <div v-if="internalApprovalUsers.length > 0">
                                    <div class="text-xs tracking-wide text-gray-500 mb-2">{{ $t('Interne Freigaben') }}</div>
                                    <div class="space-y-4">
                                        <div v-for="user in internalApprovalUsers" :key="'documentation-internal-approval-' + user.id" class="flex items-center">
                                            <img
                                                :src="user.avatar !== 'gravatar' ? user.avatar : user.gravatar"
                                                :alt="user.name || 'Mitarbeiter'"
                                                class="h-7 w-7 rounded-full object-cover mr-3 flex-shrink-0"
                                            />
                                            <div class="min-w-0 flex-1">
                                                <div class="text-sm text-gray-900 truncate">{{ user.name }}</div>
                                                <div class="text-sm leading-4 text-gray-400 truncate">{{ user.email || '-' }}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div v-if="customerApprovalUsers.length > 0" class="mt-6">
                                    <div class="text-xs tracking-wide text-gray-500 mb-2">{{ $t('Kundenfreigaben') }}</div>
                                    <div class="space-y-4">
                                        <div v-for="user in customerApprovalUsers" :key="'documentation-customer-approval-' + user.id" class="flex items-center">
                                            <img
                                                :src="user.avatar !== 'gravatar' ? user.avatar : user.gravatar"
                                                :alt="user.name || 'Kunde'"
                                                class="h-7 w-7 rounded-full object-cover mr-3 flex-shrink-0"
                                            />
                                            <div class="min-w-0 flex-1">
                                                <div class="text-sm text-gray-900 truncate">{{ user.name }}</div>
                                                <div class="text-sm leading-4 text-gray-400 truncate">{{ user.email || '-' }}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div v-if="guestApprovalUsers.length > 0" class="mt-6">
                                    <div class="text-xs tracking-wide text-gray-500 mb-2">{{ $t('Gastfreigaben') }}</div>
                                    <div class="space-y-4">
                                        <div v-for="user in guestApprovalUsers" :key="'documentation-guest-approval-' + user.id" class="flex items-center">
                                            <img
                                                :src="user.avatar !== 'gravatar' ? user.avatar : user.gravatar"
                                                :alt="user.name || 'Gast'"
                                                class="h-7 w-7 rounded-full object-cover mr-3 flex-shrink-0"
                                            />
                                            <div class="min-w-0 flex-1">
                                                <div class="text-sm text-gray-900 truncate">{{ user.name }}</div>
                                                <div class="text-sm leading-4 text-gray-400 truncate">{{ user.email || '-' }}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </aside>
                    </div>
                </div>
            </div>
        </form>
        <input ref="fileInput" hidden type="file" @change="uploadFile($event)">
        <approval-management-dialog
            :open="approvalModal"
            :internal-users="readerOptions"
            :customer-users="customerOptions"
            :guest-users="guestOptions"
            :selected-internal-ids="documentation.reader_ids"
            :selected-customer-ids="documentation.customer_ids"
            :selected-guest-ids="documentation.guest_ids"
            :show-tab-bar="isAdmin"
            :show-customer-tab="isAdmin"
            :show-guest-tab="isAdmin"
            @close="closeApprovalModal"
            @save="saveApprovalSelections"
        />

        <confirm-dialog :open="deleteDocumentationModal" title="Dokumentation löschen" text="Soll diese Seite wirklich gelöscht werden? Diese Aktion kann nicht rückgängig gemacht werden." confirm-label="Löschen" confirm-variant="danger" @close="deleteDocumentationModal = false" @confirm="deleteDocumentation" />
    </div>
</template>

<script>
import ApprovalManagementDialog from "@/components/modals/approval-management-dialog";
import ConfirmDialog from '@/components/modals/confirm-dialog.vue';
import {mixin as clickaway} from "vue-clickaway";
import {resolveAccessState} from "@/utils/access-state";
import {resolveReturnToPath} from "@/utils/list-route-state";

export default {
    name: "documentation-manage",
    components: {
        ApprovalManagementDialog,
        ConfirmDialog,
    },
    mixins: [clickaway],
    metaInfo() {
        return {
            title: "Dokumentation bearbeiten"
        }
    },
    data() {
        return {
            loading: {
                form: false,
                file: false,
            },
            uploadingFileProgress: 0,
            deleteDocumentationModal: false,
            approvalModal: false,
            isInitializingDocumentation: true,
            isDeletingDocumentation: false,
            historyPopoverOpen: false,
            dropdownSaving: false,
            dropdownSaveQueued: false,
            documentationRequestController: null,
            dropdownSaveController: null,
            isEditMode: false,
            selectedMenuAnchorId: '',
            readerOptions: [],
            customerOptions: [],
            guestOptions: [],
            documentation: {
                title: '',
                body: '',
                is_readonly: true,
                reader_ids: [],
                customer_ids: [],
                guest_ids: [],
                readers: [],
                customers: [],
                guests: [],
                attachments: [],
                change_history: [],
                can_edit: false,
                can_delete: false,
                can_manage_permissions: false,
                user: null,
            }
        };
    },
    computed: {
        accessState() {
            return resolveAccessState(this.$store);
        },
        isAdmin() {
            return this.accessState.isAdmin;
        },
        isEmployeeLike() {
            return this.accessState.isEmployeeLike;
        },
        isGuest() {
            return this.accessState.isGuest;
        },
        isServiceProvider() {
            return this.accessState.isServiceProvider;
        },
        isCustomerDocumentationRoute() {
            return String(this.$route.path || '').startsWith('/documentations');
        },
        documentationListPath() {
            const fallbackPath = this.isCustomerDocumentationRoute ? '/documentations/list' : '/workspace/documentation';
            return resolveReturnToPath(this.$route.query || {}, fallbackPath);
        },
        currentUserId() {
            const user = this.$store && this.$store.state ? this.$store.state.user : null;
            return user && user.id ? Number(user.id) : null;
        },
        isOwnDocumentation() {
            const ownerId = this.documentation && this.documentation.user ? Number(this.documentation.user.id) : null;
            return ownerId !== null && this.currentUserId !== null && ownerId === this.currentUserId;
        },
        canShowApprovalManagement() {
            return this.documentation.can_manage_permissions && (this.isAdmin || this.isEmployeeLike);
        },
        canSeeReadonlyControl() {
            return this.isAdmin || (this.isServiceProvider && this.isOwnDocumentation && this.documentation.can_manage_permissions);
        },
        menuAnchors() {
            const body = this.documentation && this.documentation.body ? String(this.documentation.body) : '';
            if (!body) {
                return [];
            }

            const parser = new DOMParser();
            const doc = parser.parseFromString(body, 'text/html');
            const nodes = Array.from(doc.querySelectorAll('[id^="doc-menu-"], h1[id], h2[id], h3[id]'));
            const anchors = [];
            const seenIds = new Set();

            nodes.forEach((node) => {
                const rawId = (node.getAttribute('id') || '').trim();
                if (!rawId || seenIds.has(rawId)) {
                    return;
                }

                const rawLabel = (node.textContent || '').trim();
                if (!rawLabel) {
                    return;
                }

                if (!rawId.startsWith('doc-menu-') && !['H1', 'H2', 'H3'].includes(node.tagName)) {
                    return;
                }

                seenIds.add(rawId);
                anchors.push({
                    id: rawId,
                    label: rawLabel,
                });
            });

            return anchors;
        },
        internalApprovalUsers() {
            const ownerId = this.documentation && this.documentation.user ? Number(this.documentation.user.id) : null;
            return (this.documentation.readers || []).filter((reader) => {
                return !(ownerId !== null && Number(reader.id) === ownerId);
            });
        },
        customerApprovalUsers() {
            const ownerId = this.documentation && this.documentation.user ? Number(this.documentation.user.id) : null;
            return (this.documentation.customers || []).filter((customer) => {
                return !(ownerId !== null && Number(customer.id) === ownerId);
            });
        },
        guestApprovalUsers() {
            const ownerId = this.documentation && this.documentation.user ? Number(this.documentation.user.id) : null;
            return (this.documentation.guests || []).filter((guest) => {
                return !(ownerId !== null && Number(guest.id) === ownerId);
            });
        },
        documentationOwnerPhone() {
            const user = this.documentation && this.documentation.user ? this.documentation.user : null;
            return user ? (user.phone || user.phone_mobile || user.phone_landline || null) : null;
        }
    },
    watch: {
        isAdmin(newValue) {
            if (newValue) {
                this.getCustomerOptions();
                this.getGuestOptions();
            }
        }
    },
    mounted() {
        this.getReaderOptions();
        this.getCustomerOptions();
        this.getGuestOptions();
        this.getDocumentation();
    },
    beforeUnmount() {
        this.cancelDocumentationRequests();
    },
    methods: {
        closeActionDropdowns() {
            this.historyPopoverOpen = false;
        },
        toggleHistoryPopover() {
            this.historyPopoverOpen = !this.historyPopoverOpen;
        },
        cancelDocumentationRequests() {
            if (this.documentationRequestController) {
                this.documentationRequestController.abort();
                this.documentationRequestController = null;
            }

            if (this.dropdownSaveController) {
                this.dropdownSaveController.abort();
                this.dropdownSaveController = null;
            }
        },
        enhanceRenderedBody() {
            this.$nextTick(function () {
                if (typeof window.__enhanceCodeBlocks === 'function') {
                    window.__enhanceCodeBlocks(this.$el || document);
                }
            });
        },
        openApprovalModal() {
            if (!this.canShowApprovalManagement) {
                return;
            }

            this.approvalModal = true;
        },
        closeApprovalModal() {
            this.approvalModal = false;
        },
        openRenamePage() {
            if (!this.isAdmin) {
                return;
            }
            this.$router.push('/workspace/documentation/' + this.$route.params.id + '/edit');
        },
        openDeleteDocumentationModal() {
            if (!this.documentation.can_delete) {
                return;
            }
            this.deleteDocumentationModal = true;
        },
        getReaderOptions() {
            const self = this;
            axios.get('api/workspace/documentations/readers').then(function (response) {
                self.readerOptions = response.data;
            });
        },
        getCustomerOptions() {
            const self = this;
            if (!self.isAdmin) {
                return;
            }
            if (self.customerOptions.length > 0) {
                return;
            }
            axios.get('api/workspace/documentations/customers').then(function (response) {
                self.customerOptions = response.data;
            });
        },
        getGuestOptions() {
            const self = this;
            if (!self.isAdmin) {
                return;
            }
            if (self.guestOptions.length > 0) {
                return;
            }
            axios.get('api/workspace/documentations/guests').then(function (response) {
                self.guestOptions = response.data;
            });
        },
        getDocumentation() {
            const self = this;
            if (self.isDeletingDocumentation) {
                return;
            }
            if (self.documentationRequestController) {
                self.documentationRequestController.abort();
            }
            self.documentationRequestController = new AbortController();
            self.loading.form = true;
            axios.get('api/workspace/documentations/' + self.$route.params.id, {
                signal: self.documentationRequestController.signal,
            }).then(function (response) {
                self.documentation = response.data;
                self.isEditMode = false;
                self.selectedMenuAnchorId = '';
                self.loading.form = false;
                self.isInitializingDocumentation = false;
                self.enhanceRenderedBody();
            }).catch(function (error) {
                if (error && error.code === 'ERR_CANCELED') {
                    return;
                }
                if (self.isDeletingDocumentation) {
                    return;
                }
                self.loading.form = false;
                self.isInitializingDocumentation = false;
                self.$router.push(self.documentationListPath);
            }).finally(function () {
                self.documentationRequestController = null;
            });
        },
        saveDocumentation() {
            const self = this;
            if (!self.documentation.can_edit) {
                return;
            }
            if (!self.isEditMode) {
                return;
            }
            self.loading.form = true;
            axios.put('api/workspace/documentations/' + self.$route.params.id, self.documentation).then(function (response) {
                self.documentation = response.data.documentation;
                self.isEditMode = false;
                self.selectedMenuAnchorId = '';
                self.loading.form = false;
                self.enhanceRenderedBody();
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data updated correctly').toString(),
                    type: 'success'
                });
            }).catch(function () {
                self.loading.form = false;
            });
        },
        startEditMode() {
            if (!this.documentation.can_edit) {
                return;
            }
            window.setTimeout(() => {
                this.isEditMode = true;
            }, 0);
        },
        onMenuAnchorChange() {
            if (!this.selectedMenuAnchorId) {
                return;
            }
            this.scrollToMenuAnchor(this.selectedMenuAnchorId);
        },
        scrollToMenuAnchor(anchorId) {
            this.$nextTick(function () {
                const element = document.getElementById(anchorId);
                if (!element) {
                    return;
                }

                const container = this.$refs.contentScrollContainer;
                if (!container) {
                    element.scrollIntoView({behavior: 'smooth', block: 'start'});
                    return;
                }

                const containerRect = container.getBoundingClientRect();
                const elementRect = element.getBoundingClientRect();
                const stickyHeaderOffset = 80;
                const nextTop = container.scrollTop + (elementRect.top - containerRect.top) - stickyHeaderOffset;

                container.scrollTo({
                    top: Math.max(0, nextTop),
                    behavior: 'smooth'
                });
            });
        },
        deleteDocumentation() {
            const self = this;
            if (!self.documentation.can_delete) {
                self.deleteDocumentationModal = false;
                return;
            }
            self.isDeletingDocumentation = true;
            self.cancelDocumentationRequests();
            self.loading.form = true;
            axios.delete('api/workspace/documentations/' + self.$route.params.id).then(function () {
                self.deleteDocumentationModal = false;
                self.loading.form = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data deleted successfully').toString(),
                    type: 'success'
                });
                self.$router.replace(self.documentationListPath);
            }).catch(function () {
                self.isDeletingDocumentation = false;
                self.loading.form = false;
                self.deleteDocumentationModal = false;
            });
        },
        selectUploadFile() {
            if (!this.documentation.can_edit) {
                return;
            }
            if (!this.loading.file) {
                this.$refs.fileInput.click();
            } else {
                this.$notify({
                    title: this.$i18n.t('Error').toString(),
                    text: this.$i18n.t('A file is being uploaded').toString(),
                    type: 'warning'
                });
            }
        },
        uploadFile(e) {
            const self = this;
            if (!self.documentation.can_edit) {
                self.$refs.fileInput.value = null;
                return;
            }
            const formData = new FormData();
            self.loading.file = true;
            formData.append('file', e.target.files[0]);
            axios.post(
                'api/workspace/documentations/attachments',
                formData,
                {
                    headers: {'Content-Type': 'multipart/form-data'},
                    onUploadProgress: function (progressEvent) {
                        self.uploadingFileProgress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                    }.bind(this)
                }
            ).then(function (response) {
                self.loading.file = false;
                self.uploadingFileProgress = 0;
                self.$refs.fileInput.value = null;
                self.documentation.attachments.push(response.data);
            }).catch(function () {
                self.loading.file = false;
                self.uploadingFileProgress = 0;
                self.$refs.fileInput.value = null;
            });
        },
        removeAttachment(attachment) {
            if (!this.documentation.can_edit) {
                return;
            }
            this.documentation.attachments.splice(attachment, 1);
        },
        saveApprovalSelections(payload) {
            if (!this.canShowApprovalManagement) {
                return;
            }

            this.documentation.reader_ids = Array.isArray(payload && payload.internal_ids) ? payload.internal_ids : [];
            if (this.isAdmin) {
                this.documentation.customer_ids = Array.isArray(payload && payload.customer_ids) ? payload.customer_ids : [];
                this.documentation.guest_ids = Array.isArray(payload && payload.guest_ids) ? payload.guest_ids : [];
            }
            this.savePermissionChanges(true);
        },
        savePermissionChanges(closeApprovalModalOnSuccess = false) {
            const self = this;
            if (self.isInitializingDocumentation) {
                return;
            }
            if (!self.documentation.can_edit) {
                return;
            }

            if (self.dropdownSaving) {
                self.dropdownSaveQueued = true;
                return;
            }

            self.dropdownSaving = true;
            if (self.dropdownSaveController) {
                self.dropdownSaveController.abort();
            }
            self.dropdownSaveController = new AbortController();
            axios.put('api/workspace/documentations/' + self.$route.params.id, self.documentation, {
                signal: self.dropdownSaveController.signal,
            }).then(function (response) {
                self.documentation = response.data.documentation;
                if (closeApprovalModalOnSuccess) {
                    self.approvalModal = false;
                }
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data updated correctly').toString(),
                    type: 'success'
                });
            }).catch(function (error) {
                if (error && error.code === 'ERR_CANCELED') {
                    return;
                }
                if (!self.isDeletingDocumentation) {
                    self.getDocumentation();
                }
            }).finally(function () {
                self.dropdownSaveController = null;
                self.dropdownSaving = false;
                if (self.dropdownSaveQueued) {
                    self.dropdownSaveQueued = false;
                    self.savePermissionChanges(closeApprovalModalOnSuccess);
                }
            });
        },
        formatDateTime(value) {
            if (!value) {
                return '-';
            }
            return window.moment(value).format('DD.MM.YYYY HH:mm');
        },
    }
}
</script>

<style lang="scss">
.documentation-permission-icons {
    color: var(--ui-text-subtle);
}

.documentation-permission-icon svg,
.documentation-permission-icon svg * {
    fill: var(--ui-text-subtle) !important;
    stroke: var(--ui-text-subtle) !important;
}

.documentation-editor .tox.tox-tinymce {
    min-height: 360px;
}

.documentation-history-popover {
    max-height: 430px;
}

.documentation-content-menu-item {
    line-height: 1.25rem;
}

.ticket-reply-body .doc-menu-anchor-chip {
    display: inline-flex;
    align-items: center;
    border: 0;
    padding: 0;
    font-weight: inherit;
}

.ticket-reply-body .doc-menu-anchor-chip.doc-menu-anchor-heading-h1 {
    font-size: 1.5rem;
    line-height: 2rem;
    font-weight: 600;
}

.ticket-reply-body .doc-menu-anchor-chip.doc-menu-anchor-heading-h2 {
    font-size: 1.25rem;
    line-height: 1.75rem;
    font-weight: 600;
}

.ticket-reply-body .doc-menu-anchor-chip::before {
    content: '';
    display: inline-block;
    vertical-align: middle;
    margin-right: 6px;
    width: 14px;
    height: 14px;
    background-repeat: no-repeat;
    background-position: center;
    background-size: 14px 14px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M6 3h12a1 1 0 0 1 1 1v17l-7-4-7 4V4a1 1 0 0 1 1-1z' stroke='%233e3f40' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round' fill='none'/%3E%3C/svg%3E");
}

.ticket-reply-body h1 {
    font-size: 1.5rem;
    line-height: 2rem;
    font-weight: 600;
    margin: 0 0 0.75rem 0;
}

.ticket-reply-body h2 {
    font-size: 1.25rem;
    line-height: 1.75rem;
    font-weight: 600;
    margin: 0 0 0.625rem 0;
}
</style>
